/**
 * Client-side game manifest map — same key set as GAME_LOADERS in
 * loaders.ts. Vite inlines each manifest.json at build time, giving the
 * shell synchronous access to per-game presentation metadata (name,
 * orientation) without waiting for the lobby's /games fetch or the game
 * bundle itself.
 *
 * `orientation` is the Total-Redesign-Spec §1b field ('landscape' |
 * 'portrait'); no manifest declares it yet, so orientation-driven UI
 * (RotateOverlay, landscape lock) stays dormant until manifests gain it.
 */
import bannerBash from '../../../../games/banner-bash/manifest.json';
import burnstile from '../../../../games/burnstile/manifest.json';
import cellarSweep from '../../../../games/cellar-sweep/manifest.json';
import cipherCellar from '../../../../games/cipher-cellar/manifest.json';
import climbSpire from '../../../../games/climb-spire/manifest.json';
import coinConveyer from '../../../../games/coin-conveyer/manifest.json';
import draftDuel from '../../../../games/draft-duel/manifest.json';
import heistHeist from '../../../../games/heist-heist/manifest.json';
import hexagonHeat from '../../../../games/hexagon-heat/manifest.json';
import lastLantern from '../../../../games/last-lantern/manifest.json';
import lumberTumble from '../../../../games/lumber-tumble/manifest.json';
import oneOfUs from '../../../../games/one-of-us/manifest.json';
import ouijaRoulette from '../../../../games/ouija-roulette/manifest.json';
import phantomPursuit from '../../../../games/phantom-pursuit/manifest.json';
import piratePulley from '../../../../games/pirate-pulley/manifest.json';
import plinkoTavern from '../../../../games/plinko-tavern/manifest.json';
import quickDraw from '../../../../games/quick-draw/manifest.json';
import quillRace from '../../../../games/quill-race/manifest.json';
import relayForge from '../../../../games/relay-forge/manifest.json';
import ropeWalk from '../../../../games/rope-walk/manifest.json';
import skySieve from '../../../../games/sky-sieve/manifest.json';
import spiceRush from '../../../../games/spice-rush/manifest.json';
import splatterSquare from '../../../../games/splatter-square/manifest.json';
import stackSmack from '../../../../games/stack-smack/manifest.json';
import stickBrawl from '../../../../games/stick-brawl/manifest.json';
import tapRace from '../../../../games/tap-race/manifest.json';
import tarotTap from '../../../../games/tarot-tap/manifest.json';
import traceRace from '../../../../games/trace-race/manifest.json';
import trapline from '../../../../games/trapline/manifest.json';
import witchSwitch from '../../../../games/witch-switch/manifest.json';

export interface ClientGameManifest {
  name: string;
  orientation?: 'landscape' | 'portrait';
  [key: string]: unknown;
}

const asManifest = (m: unknown): ClientGameManifest => m as ClientGameManifest;

export const GAME_MANIFESTS: Record<string, ClientGameManifest> = {
  'banner-bash': asManifest(bannerBash),
  'burnstile': asManifest(burnstile),
  'cellar-sweep': asManifest(cellarSweep),
  'cipher-cellar': asManifest(cipherCellar),
  'climb-spire': asManifest(climbSpire),
  'coin-conveyer': asManifest(coinConveyer),
  'draft-duel': asManifest(draftDuel),
  'heist-heist': asManifest(heistHeist),
  'hexagon-heat': asManifest(hexagonHeat),
  'last-lantern': asManifest(lastLantern),
  'lumber-tumble': asManifest(lumberTumble),
  'one-of-us': asManifest(oneOfUs),
  'ouija-roulette': asManifest(ouijaRoulette),
  'phantom-pursuit': asManifest(phantomPursuit),
  'pirate-pulley': asManifest(piratePulley),
  'plinko-tavern': asManifest(plinkoTavern),
  'quick-draw': asManifest(quickDraw),
  'quill-race': asManifest(quillRace),
  'relay-forge': asManifest(relayForge),
  'rope-walk': asManifest(ropeWalk),
  'sky-sieve': asManifest(skySieve),
  'spice-rush': asManifest(spiceRush),
  'splatter-square': asManifest(splatterSquare),
  'stack-smack': asManifest(stackSmack),
  'stick-brawl': asManifest(stickBrawl),
  'tap-race': asManifest(tapRace),
  'tarot-tap': asManifest(tarotTap),
  'trace-race': asManifest(traceRace),
  'trapline': asManifest(trapline),
  'witch-switch': asManifest(witchSwitch),
};

/** Declared orientation preference for a game, or null when the manifest
 *  doesn't declare one (fully responsive — most games today). */
export function gameOrientation(gameId: string): 'landscape' | 'portrait' | null {
  const o = GAME_MANIFESTS[gameId]?.orientation;
  return o === 'landscape' || o === 'portrait' ? o : null;
}

/** Display name from the bundled manifest (fallback when the lobby's
 *  /games list hasn't loaded or lacks the id). */
export function gameName(gameId: string): string | null {
  return GAME_MANIFESTS[gameId]?.name ?? null;
}
